import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  ArrowDownToLine,
  ArrowUpDown,
  Check,
  Download,
  GripVertical,
  Loader2,
  Upload,
} from "lucide-react"
import { useState } from "react"

import { FileDropzone } from "@/components/FileDropzone"
import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { RelatedTools } from "@/components/related-tools"
import { SEO } from "@/components/seo"
import { Button } from "@/components/ui/button"
import { useDocumentUpload } from "@/hooks/useDocumentUpload"
import { useOperationGuard } from "@/hooks/useOperationGuard"
import { usePdfReorder } from "@/hooks/usePdfReorder"
import { type PdfFile, usePdfThumbnails } from "@/hooks/usePdfThumbnails"
import { cn } from "@/lib/utils"

const howItWorks = [
  { icon: Upload, text: "Upload PDF" },
  { icon: GripVertical, text: "Drag pages to reorder" },
  { icon: ArrowDownToLine, text: "Download reordered PDF" },
]

interface Props {
  limits: {
    daily_remaining: number
    max_file_size_mb: number
    max_pages: number
  }
  authenticated: boolean
}

interface PageItem {
  id: string
  pageNumber: number
  thumbnail: string
}

function SortablePageThumbnail({ page }: { page: PageItem }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: page.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "bg-card cursor-grab rounded-lg border p-2 transition-shadow",
        isDragging && "ring-primary shadow-lg ring-2",
      )}
    >
      <img
        src={page.thumbnail}
        alt={`Page ${page.pageNumber}`}
        className="h-32 w-auto rounded object-contain"
      />
      <p className="text-muted-foreground mt-1 text-center text-xs">
        Page {page.pageNumber}
      </p>
    </div>
  )
}

export default function Reorder({ limits, authenticated }: Props) {
  const [file, setFile] = useState<PdfFile | null>(null)
  const [pages, setPages] = useState<PageItem[]>([])
  const [hasReordered, setHasReordered] = useState(false)
  const { generateThumbnails, loading: loadingThumbnails } = usePdfThumbnails()
  const { reorderPdf, processing } = usePdfReorder()
  const { uploadDocument } = useDocumentUpload()
  const {
    remainingOperations,
    completed,
    guardOperation,
    markCompleted,
    Modals,
  } = useOperationGuard({
    dailyRemaining: limits.daily_remaining,
    authenticated,
  })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleFilesAdded = async (files: File[]) => {
    if (files.length > 0) {
      const pdfFile = await generateThumbnails(files[0])
      setFile(pdfFile)
      setPages(
        pdfFile.thumbnails.map((thumb, i) => ({
          id: `page-${i + 1}`,
          pageNumber: i + 1,
          thumbnail: thumb,
        })),
      )
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setPages((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id)
        const newIndex = items.findIndex((i) => i.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
      setHasReordered(true)
    }
  }

  const handleReorder = async () => {
    if (!file) return
    if (!guardOperation()) return

    const pageOrder = pages.map((p) => p.pageNumber)
    const blob = await reorderPdf(file.file, { pageOrder })

    const filename = `${file.name.replace(".pdf", "")}_reordered.pdf`
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)

    markCompleted()
    void uploadDocument(blob, filename, "reorder_pages")
  }

  return (
    <>
      <SEO
        title="Reorder PDF Pages Online - Free Organizer | StitchPDF"
        description="Reorder PDF pages instantly with drag and drop. Rearrange, delete, or rotate pages for free. No signup required - works in your browser."
        canonical="https://stitchpdf.com/reorder-pdf"
        ogTitle="Reorder PDF Pages Online - StitchPDF"
        ogDescription="Rearrange pages in your PDF document with simple drag and drop."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Reorder PDF Pages - StitchPDF",
            description:
              "Rearrange pages in your PDF document with drag and drop.",
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Reorder PDF Pages",
            description: "Rearrange pages in a PDF document.",
            step: [
              {
                "@type": "HowToStep",
                name: "Upload PDF",
                text: "Select the PDF file with pages you want to reorder.",
              },
              {
                "@type": "HowToStep",
                name: "Drag pages",
                text: "Drag and drop page thumbnails to rearrange them.",
              },
              {
                "@type": "HowToStep",
                name: "Download reordered PDF",
                text: "Click Reorder & Download to save your reorganized PDF.",
              },
            ],
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-8">
          <div className="mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              <ArrowUpDown
                className="text-primary h-8 w-8"
                aria-hidden="true"
              />
              <h1 className="text-2xl font-bold">Reorder PDF Pages</h1>
            </div>
            <p className="text-muted-foreground mt-2">
              Rearrange pages in your PDF document
            </p>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              {howItWorks.map((step, i) => {
                const hasFile = file !== null
                const isComplete =
                  completed ||
                  (hasFile && i === 0) ||
                  (hasReordered && i === 1) ||
                  (processing && i <= 1)
                const isActive =
                  !completed &&
                  ((processing && i === 2) ||
                    (!processing &&
                      ((hasFile && !hasReordered && i === 1) ||
                        (hasFile && i === 2))))
                return (
                  <div
                    key={step.text}
                    className={`flex items-center gap-2 ${
                      isComplete
                        ? "text-green-700 dark:text-green-400"
                        : isActive
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                        isComplete
                          ? "bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-400"
                          : isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-primary/10 text-primary"
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <step.icon className="h-4 w-4" aria-hidden="true" />
                    <span>{step.text}</span>
                  </div>
                )
              })}
            </div>

            {!file ? (
              <div className="mt-8">
                <FileDropzone
                  onFilesAdded={(files) => void handleFilesAdded(files)}
                  multiple={false}
                />
              </div>
            ) : (
              <div className="mt-8">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {file.name} - {pages.length} pages
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {remainingOperations} operations remaining today
                  </p>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={pages} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-4 gap-4 md:grid-cols-6 lg:grid-cols-8">
                      {pages.map((page) => (
                        <SortablePageThumbnail key={page.id} page={page} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <div className="mt-8 flex gap-4">
                  <Button
                    onClick={() => void handleReorder()}
                    disabled={processing}
                    className="gap-2"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Reorder & Download
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {loadingThumbnails && (
              <div className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading PDF...
              </div>
            )}

            <RelatedTools currentTool="reorder-pdf" />

            <section className="mt-16 border-t pt-12">
              <h2 className="text-xl font-semibold">About This Tool</h2>
              <div className="text-muted-foreground mt-4 space-y-4 text-sm leading-relaxed">
                <p>
                  Drag page thumbnails to reorder them. Download the reorganized
                  PDF when done. Runs in your browser, so your document stays on
                  your device.
                </p>
                <h3 className="text-foreground pt-4 font-medium">Features</h3>
                <ul className="list-inside list-disc space-y-2">
                  <li>
                    <strong>Drag to reorder:</strong> See thumbnails as you
                    rearrange
                  </li>
                  <li>
                    <strong>Browser processing:</strong> Files stay on your
                    device
                  </li>
                  <li>
                    <strong>Any length:</strong> Works with documents of any
                    size
                  </li>
                  <li>
                    <strong>Original quality:</strong> No compression
                  </li>
                </ul>
                <h3 className="text-foreground pt-4 font-medium">Use Cases</h3>
                <p>
                  Fix scanned documents with pages out of order. Reorganize
                  presentations. Rearrange report sections. Restructure ebooks.
                </p>
              </div>
            </section>
          </div>
        </main>
        <PublicFooter />
      </div>

      <Modals />
    </>
  )
}
